import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaPencilAlt, FaArchive, FaTrashRestoreAlt } from "react-icons/fa";
import { MdOutlineContentCopy } from "react-icons/md";
import BeatLoader from "react-spinners/BeatLoader";
import { Box, InputBase, Paper, Tab, Tabs } from "@mui/material";
import { toast } from "react-toastify";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  createTheme,
  ThemeProvider,
  alpha,
  styled,
} from "@mui/material/styles";
import { DataGridPro, gridClasses, GridToolbar } from "@mui/x-data-grid-pro";
import DeleteModal from "../../../../components/DeleteModal";
import {
  archiveQuestionnaireService,
  copyQuestionnaireService,
  deleteQuestionnaireService,
  getAllQuestionnaireDataSevice,
  getArchivedQuestionnaireDataService,
  restoreQuestionnaireService,
} from "./Questionnaire";
import { SearchSharp } from "@mui/icons-material";

const getMuiTheme = createTheme({
  typography: {
    fontSize: 25,
  },
  ".css-1x51dt5-MuiInputBase-input-MuiInput-input": {
    color: "#000000 !important",
  },
});

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  },
}));

export const linkButtonStyle = {
  cursor: "pointer",
  textAlign: "center",
  backgroundColor: "#337ab7",
  padding: "8px",
  height: "35px",
  border: "none",
  color: "white",
  borderRadius: "5px",
  fontSize: "10px",
};

export const addButtonStyle = {
  cursor: "pointer",
  textAlign: "center",
  color: "#337ab7",
  backgroundColor: "white",
  padding: "8px",
  // fontSize: "15px",
  height: "35px",
  border: "2px solid #337ab7",
  borderRadius: "5px",
};

const Questionnaires = () => {
  const navigate = useNavigate();

  const [load, setLoad] = useState(false);

  const [activeData, setActiveData] = useState([]);
  const [unchangedActiveData, setUnchangedActiveData] = useState([]);
  const [archivedData, setArchivedData] = useState([]);
  const [unchangedArchivedData, setUnchangedArchivedData] = useState([]);
  const [tabValue, setTabValue] = useState("active");

  const [pageSize, setPageSize] = useState(10);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [copyId, setCopyId] = useState(null);
  const [archiveId, setArchiveId] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const deleteNotify = () =>
    toast.success("Questionnaire Deleted Successfully", {
      toastId: "form-creation",
    });

  const copyNotify = () =>
    toast.success("Questionnaire Copied Successfully", {
      toastId: "form-creation",
    });

  const archiveNotify = () =>
    toast.success("Questionnaire Archived Successfully", {
      toastId: "archive-success",
    });

  const restoreNotify = () =>
    toast.success("Questionnaire Restored Successfully", {
      toastId: "restore-success",
    });

  const requestFailed = (msg) =>
    toast.error(`${msg}`, {
      toastId: "requestFailed",
    });

  useEffect(() => {
    fetchAllQuestionnaires();
  }, []);

  useEffect(() => {
    if (copyId) {
      copyQuestionnaire(copyId);
    }
  }, [copyId]);

  const fetchAllQuestionnaires = async () => {
    try {
      setLoad(true);
      const [activeRes, archivedRes] = await Promise.all([
        getAllQuestionnaireDataSevice(),
        getArchivedQuestionnaireDataService(),
      ]);
      if (activeRes.status) {
        setActiveData(activeRes.data?.result);
        setUnchangedActiveData(activeRes.data?.result);
      }
      if (archivedRes.status) {
        setArchivedData(archivedRes.data?.result);
        setUnchangedArchivedData(archivedRes.data?.result);
      }
    } catch (err) {
      console.log("Error: ", err.message);
    } finally {
      setLoad(false);
    }
  };

  const mapRows = (data) =>
    data.map((row) => ({
      id: row.id,
      internalName: row.internalName,
      code: row.questionnaireTypeName,
      pages: row.diaryPages?.length,
      isArchived: row.isArchived,
      action: row.id,
      delete: row.id,
    }));

  const activeRows = mapRows(activeData);
  const archivedRows = mapRows(archivedData);

  const columns = [
    {
      field: "internalName",
      headerName: "Internal Name",
      width: 220,
      renderHeader: () => <div className="grid-heading">{"Internal Name"}</div>,
      renderCell: (params) => (
        <div className="grid-body">
          <p>{params.row.internalName}</p>
        </div>
      ),
    },
    {
      field: "code",
      headerName: "Code",
      width: 220,
      renderHeader: () => <div className="grid-heading">{"Code"}</div>,
      renderCell: (params) => (
        <div className="grid-body">
          <p>{params.row.code}</p>
        </div>
      ),
    },
    {
      field: "pages",
      headerName: "Pages",
      width: 120,
      renderHeader: () => <div className="grid-heading">{"Pages"}</div>,
      renderCell: (params) => (
        <div className="grid-body">
          <p>{params.row.pages}</p>
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      renderHeader: () => (
        <div className="grid-heading" style={{ marginLeft: "60px" }}>
          {"Action"}
        </div>
      ),
      width: 320,
      renderCell: (params) => {
        const id = params?.row.id;
        const name = params?.row?.internalName;
        const isArchived = params?.row?.isArchived;

        if (isArchived) return <></>;

        return (
          <div style={{ display: "flex", gap: "25px", alignItems: "center" }}>
            <button
              style={linkButtonStyle}
              onClick={() =>
                navigate(`/study-management/questionnaires/edit-questionnaire`, {
                  state: { id },
                })
              }
            >
              <FaPencilAlt color="white" size={15} />
            </button>
            <button
              style={linkButtonStyle}
              onClick={() => setCopyId(id)}
            >
              <MdOutlineContentCopy color="white" size={15} />
            </button>
            <button
              style={linkButtonStyle}
              onClick={() =>
                navigate(
                  `/study-management/questionnaires/survey-js-form-builder`,
                  { state: { id, name } },
                )
              }
            >
              Design Questionaire
            </button>
          </div>
        );
      },
    },
    {
      field: "delete",
      headerName: "Actions",
      renderHeader: () => <div className="grid-heading">{"Actions"}</div>,
      width: 150,
      renderCell: (params) => {
        const id = params.row.id;
        const isArchived = params.row.isArchived;

        return isArchived ? (
          <button
            title="Restore Questionnaire"
            onClick={() => handleRestoreQuestionnaire(id)}
            style={{
              cursor: "pointer",
              textAlign: "center",
              background: "#2e7d32",
              border: "none",
              borderRadius: "5px",
              padding: "10px 10px",
            }}
          >
            <FaTrashRestoreAlt color="white" style={{ fontSize: "15px" }} />
          </button>
        ) : (
          <button
            title="Archive Questionnaire"
            onClick={() => {
              setArchiveId(id);
              setShowArchiveModal(true);
            }}
            style={{
              cursor: "pointer",
              textAlign: "center",
              background: "red",
              border: "none",
              borderRadius: "5px",
              padding: "10px 10px",
            }}
          >
            <FaArchive color="white" style={{ fontSize: "15px" }} />
          </button>
        );
      },
    },
  ];

  const copyQuestionnaire = async (copyId) => {
    setLoad(true);
    try {
      const res = await copyQuestionnaireService(copyId);
      if (res?.status) {
        setLoad(false);
        copyNotify();
        fetchAllQuestionnaires();
      }
    } catch (err) {
      setLoad(false);
      console.log("Error :", err?.message);
      requestFailed(err?.message);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoad(true);
      const res = await deleteQuestionnaireService(deleteId);
      if (res?.data?.status == "Failed") {
        requestFailed(
          "Questionnaire ID is in used cannot delete this questionnaire!",
        );
        fetchAllQuestionnaires();
        setShowConfirmModal(false);
      } else if (res?.data?.status == "Success") {
        setLoad(false);
        fetchAllQuestionnaires();
        setShowConfirmModal(false);
        deleteNotify();
      }
    } catch (err) {
      console.log("Error: ", err.message);
      setLoad(false);
      setShowConfirmModal(false);
      requestFailed(err.message);
    }
  };

  const handleArchiveConfirm = async () => {
    try {
      setLoad(true);
      const res = await archiveQuestionnaireService(archiveId);
      setShowArchiveModal(false);
      if (res?.data?.status === "Success") {
        archiveNotify();
        fetchAllQuestionnaires();
      } else {
        requestFailed("Unable to archive questionnaire!");
      }
    } catch (err) {
      requestFailed(err?.message);
    } finally {
      setLoad(false);
      setArchiveId(null);
    }
  };

  const handleRestoreQuestionnaire = async (id) => {
    try {
      setLoad(true);
      const res = await restoreQuestionnaireService(id);
      if (res?.data?.status === "Success") {
        restoreNotify();
        fetchAllQuestionnaires();
      } else {
        requestFailed("Unable to restore questionnaire!");
      }
    } catch (err) {
      requestFailed(err?.message);
    } finally {
      setLoad(false);
    }
  };

  const handleQuestionnaireSearch = (value) => {
    const filterFn = (item) => {
      const internalNameMatch = item.internalName
        ?.toLowerCase()
        .includes(value.toLowerCase());
      const codeMatch = item.questionnaireTypeName
        ?.toLowerCase()
        .includes(value.toLowerCase());
      return internalNameMatch || codeMatch;
    };
    if (tabValue === "active") {
      setActiveData(unchangedActiveData.filter(filterFn));
    } else {
      setArchivedData(unchangedArchivedData.filter(filterFn));
    }
  };

  return (
    <>
      {load ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
            }}
          >
            <BeatLoader color="#3661eb" />
          </div>
        </>
      ) : (
        <div className="content-body">
          <p className="study-management-link" style={{ fontWeight: "600" }}>
            <Link to="/study-management">Manage Your Study</Link> |{" "}
            <Link to="/study-management/questionnaires">Questionnaires</Link> -
            <b style={{ color: "gray" }}>
              {sessionStorage?.getItem("studyName")}
            </b>
          </p>
          <Row style={{ marginTop: "2%", marginBottom: "2%" }}>
            <Col md={6}>
              <div className="study-management-head-start">
                <p className="study-management-heading">Questionnaires</p>
              </div>
            </Col>
            <Col md={4}>
              <Paper
                sx={{
                  p: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <SearchSharp style={{ width: 20, height: 20 }} />
                <InputBase
                  sx={{ ml: 1, flex: 1, fontSize: 16 }}
                  placeholder="Search Questionnaires"
                  inputProps={{ "aria-label": "search questionnaires" }}
                  onChange={(e) => handleQuestionnaireSearch(e.target.value)}
                />
              </Paper>
            </Col>
            <Col md={2}>
              <Link to="/study-management/questionnaires/create-questionnaire">
                <div className="study-management-head-end">
                  <button className="study-management-create-btn-md">
                    Create Questionnaire
                  </button>
                </div>
              </Link>
            </Col>
          </Row>

          <Tabs
            value={tabValue}
            onChange={(e, newVal) => setTabValue(newVal)}
            sx={{
              marginBottom: "16px",
              "& .MuiTabs-indicator": {
                height: "3px",
                backgroundColor: "#3661eb",
              },
            }}
          >
            <Tab
              label="Active Questionnaires"
              value="active"
              sx={{
                fontFamily: "Poppins",
                fontStyle: "normal",
                fontWeight: tabValue === "active" ? 700 : 500,
                fontSize: tabValue === "active" ? "16px" : "12px",
                lineHeight: "24px",
                color: tabValue === "active" ? "#5c5c5c" : "rgba(92, 92, 92, 0.7)",
                textTransform: "capitalize",
                "&.Mui-selected": {
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "#5c5c5c",
                },
              }}
            />
            <Tab
              label="Archived Questionnaires"
              value="archived"
              sx={{
                fontFamily: "Poppins",
                fontStyle: "normal",
                fontWeight: tabValue === "archived" ? 700 : 500,
                fontSize: tabValue === "archived" ? "16px" : "12px",
                lineHeight: "24px",
                color: tabValue === "archived" ? "#5c5c5c" : "rgba(92, 92, 92, 0.7)",
                textTransform: "capitalize",
                "&.Mui-selected": {
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "#5c5c5c",
                },
              }}
            />
          </Tabs>

          <Box sx={{ height: 550, width: "100%", display: "flex" }}>
            <ThemeProvider theme={getMuiTheme}>
              <StripedDataGrid
                className="allQuestionnaire-grid"
                rows={tabValue === "active" ? activeRows : archivedRows}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 20, 30]}
                components={{ Toolbar: GridToolbar }}
                pagination
                paginationMode="client"
                rowHeight={44}
                disableSelectionOnClick
                experimentalFeatures={{ newEditingApi: true }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
              />
            </ThemeProvider>
          </Box>

          <Row style={{ marginTop: "2%" }}>
            <Col md={6}></Col>
            <Col md={6}>
              <Link to="/study-management">
                <div className="study-management-head-end">
                  <button className="study-management-create-btn-lg">
                    Back
                  </button>
                </div>
              </Link>
            </Col>
          </Row>
        </div>
      )}
      {showConfirmModal && (
        <DeleteModal
          open={showConfirmModal}
          handleClose={() => setShowConfirmModal(false)}
          handleDelete={handleDeleteConfirm}
          subject={"Questionnaire"}
        />
      )}
      {showArchiveModal && (
        <DeleteModal
          open={showArchiveModal}
          handleClose={() => {
            setShowArchiveModal(false);
            setArchiveId(null);
          }}
          handleDelete={handleArchiveConfirm}
          subject={"Questionnaire (archive)"}
        />
      )}
    </>
  );
};

export default Questionnaires;
